import { useEffect, useState } from 'react';
import {
  ChiTietPhieuKeToanTheoNghiepVu,
  ChiTietPhieuKeToanTheoNghiepVuResponse,
  PhieuKeToanTheoNghiepVu,
  PhieuKeToanTheoNghiepVuInput,
  PhieuKeToanTheoNghiepVuResponse,
  ThuePhieuKeToanTheoNghiepVu,
  ThuePhieuKeToanTheoNghiepVuResponse
} from '@/types/schemas/phieu-ke-toan-theo-nghiep-vu.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UsePhieuKeToanTheoNghiepVuReturn {
  phieuKeToanTheoNghiepVus: PhieuKeToanTheoNghiepVu[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  addPhieuKeToanTheoNghiepVu: (
    newPhieuKeToanTheoNghiepVu: PhieuKeToanTheoNghiepVuInput
  ) => Promise<PhieuKeToanTheoNghiepVu>;
  updatePhieuKeToanTheoNghiepVu: (
    uuid: string,
    updatedPhieuKeToanTheoNghiepVu: PhieuKeToanTheoNghiepVuInput
  ) => Promise<PhieuKeToanTheoNghiepVu>;
  deletePhieuKeToanTheoNghiepVu: (uuid: string) => Promise<void>;
  refreshPhieuKeToanTheoNghiepVus: () => Promise<void>;
  getPhieuKeToanTheoNghiepVuByCode: (code: string) => Promise<PhieuKeToanTheoNghiepVu | null>;
  getPhieuKeToanTheoNghiepVuById: (uuid: string) => Promise<PhieuKeToanTheoNghiepVu | null>;
  getPhieuKeToanTheoNghiepVuDetail: (uuid: string) => Promise<ChiTietPhieuKeToanTheoNghiepVu[]>;
  getPhieuKeToanTheoNghiepVuByStatus: (status: string) => Promise<PhieuKeToanTheoNghiepVu[]>;
  getPhieuKeToanTheoNghiepVuByDateRange: (fromDate: string, toDate: string) => Promise<PhieuKeToanTheoNghiepVu[]>;
}

/**
 * Hook for managing PhieuKeToanTheoNghiepVu (Accounting Voucher by Business Operation) data
 *
 * This hook provides functions to fetch, create, update, and delete accounting vouchers,
 * as well as manage their details and tax information.
 */
export const usePhieuKeToanTheoNghiepVu = (
  initialPhieuKeToanTheoNghiepVus: PhieuKeToanTheoNghiepVu[] = []
): UsePhieuKeToanTheoNghiepVuReturn => {
  const [phieuKeToanTheoNghiepVus, setPhieuKeToanTheoNghiepVus] = useState<PhieuKeToanTheoNghiepVu[]>(
    initialPhieuKeToanTheoNghiepVus
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  const fetchPhieuKeToanTheoNghiepVus = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuKeToanTheoNghiepVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`
      );
      setPhieuKeToanTheoNghiepVus(response.data.results);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching accounting vouchers:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuKeToanTheoNghiepVu = async (
    newPhieuKeToanTheoNghiepVu: PhieuKeToanTheoNghiepVuInput
  ): Promise<PhieuKeToanTheoNghiepVu> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuKeToanTheoNghiepVu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`,
        newPhieuKeToanTheoNghiepVu
      );
      setPhieuKeToanTheoNghiepVus(prev => [...prev, response.data]);
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error adding accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi thêm phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuKeToanTheoNghiepVu = async (
    uuid: string,
    updatedPhieuKeToanTheoNghiepVu: PhieuKeToanTheoNghiepVuInput
  ): Promise<PhieuKeToanTheoNghiepVu> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuKeToanTheoNghiepVu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/${uuid}/`,
        updatedPhieuKeToanTheoNghiepVu
      );
      setPhieuKeToanTheoNghiepVus(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error updating accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi cập nhật phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuKeToanTheoNghiepVu = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/${uuid}/`);
      setPhieuKeToanTheoNghiepVus(prev => prev.filter(item => item.uuid !== uuid));
      setError(null);
    } catch (error: any) {
      console.error('Error deleting accounting voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi xóa phiếu kế toán');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuKeToanTheoNghiepVuByCode = async (code: string): Promise<PhieuKeToanTheoNghiepVu | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanTheoNghiepVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`,
        {
          params: { i_so_ct: code }
        }
      );
      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error) {
      console.error('Error fetching accounting voucher by code:', error);
      return null;
    }
  };

  const getPhieuKeToanTheoNghiepVuDetail = async (uuid: string): Promise<ChiTietPhieuKeToanTheoNghiepVu[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<ChiTietPhieuKeToanTheoNghiepVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/${uuid}/chi-tiet/`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting voucher detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const getPhieuKeToanTheoNghiepVuById = async (uuid: string): Promise<PhieuKeToanTheoNghiepVu | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanTheoNghiepVu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching accounting voucher by ID:', error);
      return null;
    }
  };

  const getPhieuKeToanTheoNghiepVuByStatus = async (status: string): Promise<PhieuKeToanTheoNghiepVu[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanTheoNghiepVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`,
        {
          params: { status }
        }
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting vouchers by status:', error);
      return [];
    }
  };

  const getPhieuKeToanTheoNghiepVuByDateRange = async (
    fromDate: string,
    toDate: string
  ): Promise<PhieuKeToanTheoNghiepVu[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuKeToanTheoNghiepVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`,
        {
          params: {
            ngay_ct__gte: fromDate,
            ngay_ct__lte: toDate
          }
        }
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching accounting vouchers by date range:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchPhieuKeToanTheoNghiepVus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuKeToanTheoNghiepVus,
    isLoading,
    isLoadingDetail,
    error,
    addPhieuKeToanTheoNghiepVu,
    updatePhieuKeToanTheoNghiepVu,
    deletePhieuKeToanTheoNghiepVu,
    refreshPhieuKeToanTheoNghiepVus: fetchPhieuKeToanTheoNghiepVus,
    getPhieuKeToanTheoNghiepVuByCode,
    getPhieuKeToanTheoNghiepVuById,
    getPhieuKeToanTheoNghiepVuDetail,
    getPhieuKeToanTheoNghiepVuByStatus,
    getPhieuKeToanTheoNghiepVuByDateRange
  };
};

export const fetchAllPhieuKeToanTheoNghiepVu = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<PhieuKeToanTheoNghiepVu[]> => {
  let page = 1;
  let all: PhieuKeToanTheoNghiepVu[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuKeToanTheoNghiepVuResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_KE_TOAN_THEO_NGHIEP_VU}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
